import { useState } from 'react';
import { motion } from 'framer-motion';
import { FiSend, FiCheckCircle, FiAlertCircle, FiMail } from 'react-icons/fi';
import './Contact.css';

const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';
const WEB3FORMS_ACCESS_KEY = '0fd823dd-007c-450f-a8ca-a01424f8feb2';

function Contact() {
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const name = formData.get('name');
    const email = formData.get('email');
    const message = formData.get('message');

    setStatus('loading');
    setErrorMessage('');

    try {
      const res = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          name,
          email,
          message,
          from_name: 'Portfolio Contact',
        }),
      });
      const data = await res.json();

      if (data.success) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <section id="contact" className="contact section">
      <motion.div className="section-title-wrap" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
        <FiMail className="section-title-icon" aria-hidden />
        <h2 className="section-title section-title--inline">Contact Me!</h2>
      </motion.div>
      <motion.p
        className="section-subtitle"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        Have a project in mind or want to chat? Send a message.
      </motion.p>
      <motion.div
        className="contact__box"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {status === 'success' && (
          <motion.div
            className="contact__message contact__message--success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <FiCheckCircle className="contact__message-icon" aria-hidden />
            <h3>Message sent successfully!</h3>
            <p>Thanks for reaching out. I&apos;ll get back to you soon.</p>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div
            className="contact__message contact__message--error"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <FiAlertCircle className="contact__message-icon" aria-hidden />
            <h3>Failed to send</h3>
            <p>{errorMessage}</p>
            <motion.button
              type="button"
              className="contact__retry"
              onClick={() => { setStatus('idle'); setErrorMessage(''); }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Try again
            </motion.button>
          </motion.div>
        )}

        {(status === 'idle' || status === 'loading') && (
          <form className="contact__form" onSubmit={handleSubmit}>
            <div className="contact__row">
              <label htmlFor="name">Name</label>
              <input id="name" name="name" type="text" required placeholder="Your name" disabled={status === 'loading'} />
            </div>
            <div className="contact__row">
              <label htmlFor="email">Email</label>
              <input id="email" name="email" type="email" required placeholder="you@example.com" disabled={status === 'loading'} />
            </div>
            <div className="contact__row">
              <label htmlFor="message">Message</label>
              <textarea id="message" name="message" required rows={5} placeholder="Your message..." disabled={status === 'loading'} />
            </div>
            <motion.button
              type="submit"
              className="contact__submit"
              disabled={status === 'loading'}
              whileHover={status === 'loading' ? {} : { scale: 1.02 }}
              whileTap={status === 'loading' ? {} : { scale: 0.98 }}
            >
              {status === 'loading' ? (
                <>
                  <span className="contact__spinner" aria-hidden />
                  Sending...
                </>
              ) : (
                <>
                  <FiSend className="contact__submit-icon" aria-hidden />
                  Send message
                </>
              )}
            </motion.button>
          </form>
        )}
      </motion.div>
    </section>
  );
}

export default Contact;
